import type { HexTileMap, HexTileMapKey } from '../hexmap/model/types';

/**
 * Per-player explored tiles, stored in SimState as a hex string bitset so
 * snapshots stay small (~1 char per 4 tiles). Bit i is the i-th map key in
 * sorted order, which every peer derives identically from the same terrain.
 */

interface TileIndex {
  keys: HexTileMapKey[];
  index: Map<HexTileMapKey, number>;
}

// Pure memo: the index is a function of the (immutable) terrain map.
const indexCache = new WeakMap<HexTileMap, TileIndex>();

function tileIndex(terrain: HexTileMap): TileIndex {
  const cached = indexCache.get(terrain);
  if (cached) return cached;
  const keys = [...terrain.keys()].sort();
  const index = new Map(keys.map((k, i) => [k, i]));
  const built = { keys, index };
  indexCache.set(terrain, built);
  return built;
}

function decode(terrain: HexTileMap, encoded: string | undefined): Uint8Array {
  const { keys } = tileIndex(terrain);
  const bits = new Uint8Array(keys.length);
  if (!encoded) return bits;
  for (let i = 0; i < keys.length; i++) {
    const nibble = Number.parseInt(encoded[i >> 2] ?? '0', 16);
    bits[i] = (nibble >> (i & 3)) & 1;
  }
  return bits;
}

function encode(bits: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bits.length; i += 4) {
    const nibble = bits[i] | (bits[i + 1] << 1) | (bits[i + 2] << 2) | (bits[i + 3] << 3);
    out += nibble.toString(16);
  }
  return out;
}

/** Adds `keys` (off-map keys are ignored) to an encoded set; returns the new encoding. */
function addExplored(
  terrain: HexTileMap,
  encoded: string | undefined,
  keys: Iterable<HexTileMapKey>
): string {
  const { index } = tileIndex(terrain);
  const bits = decode(terrain, encoded);
  for (const key of keys) {
    const i = index.get(key);
    if (i !== undefined) bits[i] = 1;
  }
  return encode(bits);
}

/** Membership test over an encoded set (decodes once per call to this factory). */
function exploredLookup(
  terrain: HexTileMap,
  encoded: string | undefined
): (key: HexTileMapKey) => boolean {
  const { index } = tileIndex(terrain);
  const bits = decode(terrain, encoded);
  return (key) => {
    const i = index.get(key);
    return i !== undefined && bits[i] === 1;
  };
}

/** All explored keys of an encoded set. */
function exploredKeys(terrain: HexTileMap, encoded: string | undefined): HexTileMapKey[] {
  const { keys } = tileIndex(terrain);
  const bits = decode(terrain, encoded);
  return keys.filter((_, i) => bits[i] === 1);
}

export { addExplored, exploredKeys, exploredLookup };
